import * as React from 'react';
import {useContext} from 'react';
import {
  view,
  animate,
  createViewConfig,
  toIntent,
  assignProps,
} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import type {Style} from '@sail/engine';
import type {View} from '../../view';
import {css} from '../../css';
import {Layer} from '../layers/Layer';
import {Backdrop} from '../../components/Backdrop';
import {FocusScope} from '../layers/FocusScope';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {useRpcMediaMaxWidth} from '../hooks/use-match-media/useRpcMatchMedia';
import {useMediaQuery} from '../../providers/UiMediaQueryProvider';
import {
  desktopFullscreenSheetStyles,
  type BottomSheetVariant,
} from '../../utilStyles/sheet';

const drawerIframeOverrides = toIntent([assignProps(useOverlayIframe)]);

export const DrawerContext = React.createContext<
  | {
      onClose: () => void;
      bottomSheetVariant?: BottomSheetVariant;
      registerLabelId: (id?: string) => void;
      registerDescriptionId: (id?: string) => void;
    }
  | undefined
>(undefined);

export function useDrawerClose(): (() => void) | undefined {
  const context = useContext(DrawerContext);
  return context ? context.onClose : undefined;
}

export type DrawerSize = 'small' | 'medium' | 'large';

export type BaseDrawerProps = View.IntrinsicElement<'div'> & {
  /**
   * Block interaction with the content behind the drawer when it is open. Defaults to `true`.
   */
  blocking?: boolean;
  /**
   * The the size of the drawer's width.
   */
  size?: DrawerSize;

  /**
   * The direction the drawer slides in from and stays at when open.
   * @defaultValue 'right'
   */
  direction?: 'left' | 'right';

  inline?: boolean;
  layerName?: string;
  /**
   * Called when the drawer closes.
   */
  onClose: () => void;
  /**
   * Header content of the drawer.
   */
  header?: React.ReactNode;
  /**
   * Content shown before the children in the drawer body.
   */
  start?: React.ReactNode;
  bottomSheetStartTabTitle?: React.ReactNode;
  /**
   * The primary content of the drawer body.
   */
  children?: React.ReactNode;
  bottomSheetChildrenTabTitle?: React.ReactNode;
  /**
   * Content shown after the children in the drawer body.
   */
  end?: React.ReactNode;
  bottomSheetEndTabTitle?: React.ReactNode;
  /**
   * Restores focus to the element that triggered the drawer. Defaults to `true`.
   */
  restoreFocus?: boolean;
  subviews?: View.Subviews<{
    layer: typeof Layer;
    content: 'div';
    header: 'div';
  }>;
};

export const BaseDrawerConfig = createViewConfig({
  name: 'BaseDrawer',
  props: {} as BaseDrawerProps,
  defaults: {
    size: 'small',
    blocking: true,
    inline: false,
    restoreFocus: true,
  },
});

const minWidths: Record<DrawerSize, number> = {
  small: 344,
  medium: 480,
  large: 584,
};

const minWidthsStyles: Record<DrawerSize, Style.Intent> = {
  small: css({
    width: 'clamp(min(344px, 100vw), 20vw, 100vw)',
  }),
  medium: css({
    width: 'clamp(min(480px, 100vw), 35vw, 100vw)',
  }),
  large: css({
    width: 'clamp(min(584px, 100vw), 45vw, 100vw)',
  }),
};

function getSizeBasedMediaQueries(
  shouldDisplayMobileUI: boolean,
  size: DrawerSize = 'small',
  extra: Partial<
    Record<DrawerSize, Partial<Record<'mobile' | 'desktop', Style.Intent>>>
  > = {},
): ReturnType<typeof css>[] {
  const styles: Style.Intent[] = [];
  const sizeExtraCss = extra[size] ?? {};

  if (shouldDisplayMobileUI) {
    styles.push(css({width: 'fill'}), sizeExtraCss.mobile ?? css({}));
  } else {
    styles.push(minWidthsStyles[size], sizeExtraCss.desktop ?? css({}));
  }

  return styles;
}

// If the `end` content (preview) renders below this size, instead of side scrolling within its own
// container, the entire drawer will side scroll.
const MINIMUM_PREVIEW_SIZE = 250;
const ANIMATION_DURATION_SHORT = 100;
const ANIMATION_EASING = 'cubic-bezier(0, .09, .4, 1)';

const directionRightNonInlineStyles = css({
  borderRadius: `${$['border.radius.small']} 0 0 ${$['border.radius.small']}`,
  boxShadow: 'overlay',
});

const directionLeftNonInlineStyles = css({
  borderRadius: `0 ${$['border.radius.small']} ${$['border.radius.small']} 0`,
  boxShadow: 'overlay',
});

const directionRightStyles = css({
  right: 'space.0',
  transformOrigin: 'center left',
  borderRightWidth: 0,
});

const directionLeftStyles = css({
  transformOrigin: 'center right',
  borderLeftWidth: 0,
});

/**
 * Returns true if `start` (and `preview` in CreationDrawer) should have their own horizontal scroll.
 * Otherwise, the entire drawer will scroll horizontally.
 */
export const useShouldScroll = (size: DrawerSize = 'small') => {
  return useRpcMediaMaxWidth(`${minWidths[size] + MINIMUM_PREVIEW_SIZE}px`);
};

export const BaseDrawer = BaseDrawerConfig.createView(
  ({
    blocking = true,
    autoFocus,
    size,
    inline = false,
    layerName,
    onClose,
    header,
    start,
    bottomSheetStartTabTitle,
    children,
    bottomSheetChildrenTabTitle,
    end,
    bottomSheetEndTabTitle,
    subviews,
    restoreFocus = true,
    direction = 'right',
    ...props
  }) => {
    const uiConfig = useUiConfig();
    const {isSmallScreen} = useMediaQuery();
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;
    const shouldScroll = useShouldScroll(size);

    return (
      <Layer
        inherits={subviews.layer}
        name={layerName || (blocking ? 'modal' : 'nonmodal')}
        uses={shouldApplyIframeOverrides ? drawerIframeOverrides : undefined}
      >
        {blocking ? (
          <Backdrop
            onClose={onClose}
            uses={[
              animate.in({
                duration: ANIMATION_DURATION_SHORT,
                opacity: 0,
                easing: ANIMATION_EASING,
              }),
              animate.out({
                duration: ANIMATION_DURATION_SHORT,
                opacity: 0,
                easing: ANIMATION_EASING,
              }),
            ]}
          />
        ) : null}
        <FocusScope
          autoFocus={autoFocus}
          contain={blocking}
          restoreFocus={restoreFocus}
        >
          <view.div
            tabIndex={-1}
            role="dialog"
            uses={[
              direction === 'right' && inline === false
                ? directionRightNonInlineStyles
                : null,
              direction === 'left' && inline === false
                ? directionLeftNonInlineStyles
                : null,
              direction === 'right' ? directionRightStyles : null,
              direction === 'left' ? directionLeftStyles : null,
              // TODO: Remove this once BottomSheets are GA
              desktopFullscreenSheetStyles,
            ]}
            {...props}
          >
            <view.div
              inherits={subviews.header}
              css={[
                css({
                  height: 'fit',
                }),
                ...getSizeBasedMediaQueries(!!isSmallScreen, size),
              ]}
            >
              {header}
            </view.div>
            <view.div
              css={[
                css({
                  stack: 'x',
                  alignY: 'stretch',
                  minHeight: 'space.0',
                  height: 'fill',
                }),
                shouldScroll ? css({overflowX: 'scroll'}) : css({}),
              ]}
            >
              {start}
              <view.div
                css={[
                  ...getSizeBasedMediaQueries(!!isSmallScreen, size, {
                    small: {
                      mobile: css({
                        font: 'body.medium',
                      }),
                      desktop: css({
                        font: 'body.small',
                      }),
                    },
                  }),
                  css({
                    stack: 'y',
                    height: 'fill',
                    position: 'relative',
                    overflow: 'auto',
                  }),
                ]}
                inherits={subviews.content}
                uses={[shouldScroll ? css({flexShrink: '0'}) : null]}
              >
                {children}
              </view.div>
              {end}
            </view.div>
          </view.div>
        </FocusScope>
      </Layer>
    );
  },
  {
    // Fixed position is important here, not just to make sure the drawer always
    // appears correctly regardless of scrolling, but also for animation
    // performance. Fixed is guaranteed to not effect scrolling of the window, so
    // when the content goes offscreen the animation is smoother
    css: {
      borderColor: 'border',
      borderWidth: 'small',
      borderStyle: 'solid',
      backgroundColor: 'surface',
      color: 'text',
      position: 'fixed',
      top: 'calc(var(--sail-layer-margin-top, 0px) - 1px)',
      bottom: 'calc(var(--sail-layer-margin-bottom, 0px) - 1px)',
      transformOrigin: 'center left',
      overflow: 'hidden',
      stack: 'y',
      focusRing: 'none',
      maxWidth: '100vw',
      outline: 'none',
    },
  },
);
